import React, { useCallback, useEffect, useState } from 'react'
import {
    Button,
    Checkbox,
    Form,
    Input,
    Select,
    Upload,
    Row,
    Col,
    message,
    Typography,
    Modal,
    Alert,
    Space,
    Divider
} from 'antd';
import { InboxOutlined, InfoCircleOutlined, DownloadOutlined, ExperimentOutlined } from '@ant-design/icons';
import axios from 'axios';
import API_ROUTES from '../../../api';
import GsrdWch from '../../../components/GsrdWch';

const { Title, Paragraph, Text } = Typography;

export default function HomeUpload({ onPageChange }) {

    const { Dragger } = Upload;
    const [form] = Form.useForm();
    const [uploadFileName, setUploadFileName] = useState("");
    const [messageApi, contextHolder] = message.useMessage();
    const [demoButtonLoading, setDemoButtonLoading] = useState(false)
    const [submitButtonLoading, setSubmitButtonLoading] = useState(false)
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [imputationQuota, setImputationQuota] = useState();

    const fetchImputationQuota = () => {
        axios.get(API_ROUTES.api_imputation_quota, {
            headers: {
                Authorization: localStorage.getItem('userInfo'),
            },
        }).then((response) => {
            if (response.data.code === 200) {
                setImputationQuota(response.data.data);
            }
        });
    };

    useEffect(() => {
        fetchImputationQuota();
    }, []);

    const normFile = (e) => {
        if (Array.isArray(e)) {
            return e;
        }
        return e?.fileList;
    };

    const uploadProps = {
        name: 'file',
        multiple: true,
        onChange(info) {
            const { status } = info.file;
            if (status !== 'uploading') {
                console.log(info.file, info.fileList);
            }
            if (status === 'done') {
                message.success(`${info.file.name} file uploaded successfully.`);
            } else if (status === 'error') {
                message.error(`${info.file.name} file upload failed.`);
            }
        },
        onDrop(e) {
            console.log('Dropped files', e.dataTransfer.files);
        },
        beforeUpload: (file) => {
            const isLt10M = file.size / 1024 / 1024 < 100;
            if (!isLt10M) {
                message.error('File must be smaller than 100MB!');
            }
            return isLt10M;
        },
    };

    const formFinishFunc = (value) => {
        messageApi.open({
            type: 'loading',
            content: 'Fetching...',
        });
        setSubmitButtonLoading(true)
        axios.post(API_ROUTES.api_gene_send, {
            uploadFileName: uploadFileName.uploadFileName,
            uploadFile: uploadFileName.uploadFile,
            taskName: value.taskName,
            referencePanel: value.ReferencePanel,
            mode: value.mode,
            chr: value.chr
        }, {
            headers: {
                Authorization: localStorage.getItem('userInfo'),
            },
        })
            .then(function (response) {
                if (response.data.code === 200) {
                    messageApi.open({
                        type: 'success',
                        content: 'Submit successfully!',
                    });
                    setImputationQuota(response.data.data?.quota);
                    onPageChange('upload');
                } else {
                    messageApi.open({
                        type: 'error',
                        content: response.data.msg || 'Submit fail! Please try again!',
                    });
                    if (response.data.data) {
                        setImputationQuota(response.data.data);
                    }
                }
            })
            .catch(function (error) {
                messageApi.open({
                    type: 'error',
                    content: 'Submit fail! Please try again!',
                });
            })
            .finally(() => {
                setSubmitButtonLoading(false)
            })
    }

    const customRequestFunc = async ({ file, onSuccess, onError }) => {

        try {
            const formData = new FormData();
            formData.append('file', file);
            const response = await axios.post(API_ROUTES.api_xiaoai_upload, formData, {
                headers: {
                    Authorization: localStorage.getItem('userInfo'),
                },
            });
            if (response.data.code === 200) {
                setUploadFileName(response.data.data)
                onSuccess("ok");
            } else {
                onError("Upload failed");
            }
        } catch (error) {
            console.error('Upload error:', error);
            onError("Error occurred");
        }
    };

    const formLabelStyle = { fontSize: '16px', fontWeight: '600', color: '#055878' };
    const isQuotaLimited = imputationQuota && !imputationQuota.isAdmin && imputationQuota.remainingToday <= 0;

    return (
        <Row justify={'center'}>
            <Col xs={24} sm={22} md={20}>
                {contextHolder}

                {/* Header Section */}
                <div style={{ marginBottom: '32px' }}>
                    <Title level={2} style={{ color: '#055878', fontWeight: 800, marginBottom: '16px' }}>
                        Genotype Imputation <span style={{ fontSize: '18px', fontWeight: 400, color: '#666' }}>(Beagle 5.4)</span>
                    </Title>
                    <Alert
                        message="Requirements"
                        description={
                            <Paragraph style={{ marginBottom: 0, fontSize: '14px' }}>
                                Submit VCF files for phasing and genotype imputation using the <GsrdWch /> reference panel.
                                <br />
                                • <strong>GRCh37/hg19</strong>: chromosome names should not include the chr prefix, for example 20.<br />
                                • <strong>GRCh38/hg38</strong>: chromosome names should include the chr prefix, for example chr20.
                            </Paragraph>
                        }
                        type="info"
                        showIcon
                        icon={<InfoCircleOutlined />}
                        style={{ borderRadius: '12px', backgroundColor: '#f0f9ff', border: '1px solid #bae7ff' }}
                    />
                    {/* 今日提交配额由后端强校验，这里只做用户可见提示 */}
                    {imputationQuota && (
                        <Alert
                            message={imputationQuota.isAdmin ? 'Daily submission quota: Unlimited for administrators' : `Daily submission quota: ${imputationQuota.usedToday} used · ${imputationQuota.remainingToday} remaining`}
                            description={imputationQuota.isAdmin ? 'Administrator accounts are not limited by the daily imputation submission quota.' : 'Each user can submit up to 3 imputation jobs per day.'}
                            type={isQuotaLimited ? 'warning' : 'success'}
                            showIcon
                            style={{ marginTop: '14px', borderRadius: '12px' }}
                        />
                    )}
                </div>

                <Form
                    form={form}
                    layout='vertical'
                    onFinish={formFinishFunc}
                    initialValues={{
                        dataComplianceAgreement: false,
                        incidentReportAgreement: false,
                        mode: "1",
                    }}
                    requiredMark={false}
                >
                    <Row gutter={[24, 0]}>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item
                                label={<span style={formLabelStyle}>Submission name</span>}
                                name="taskName"
                                rules={[{ required: true, message: 'Please input project name!' }]}
                            >
                                <Input size="large" placeholder="Enter project identifier" style={{ borderRadius: '8px' }} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item
                                label={<span style={formLabelStyle}>Reference Panel</span>}
                                name="ReferencePanel"
                                rules={[{ required: true, message: 'Please select panel!' }]}
                            >
                                <Select size="large" placeholder="Choose Reference Panel" style={{ borderRadius: '8px' }}>
                                    {/* 前两个 GSRD 面板选项统一展示 WCH 右上角角标 */}
                                    <Select.Option value="2"><span><GsrdWch /> GRCh38 / hg38</span></Select.Option>
                                    <Select.Option value="1"><span><GsrdWch /> GRCh37 / hg19</span></Select.Option>
                                    {/* 1000G 面板 value 需与后端 MissionTask 中的脚本分支保持一致 */}
                                    <Select.Option value="3">1000G Phase 3 v5 (GRCh38/hg38)</Select.Option>
                                    <Select.Option value="4">1000G Phase 3 v5 (GRCh37/hg19)</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item
                                label={<span style={formLabelStyle}>Chromosome</span>}
                                name="chr"
                                rules={[{ required: true, message: 'Please choose chr!' }]}
                            >
                                <Select size="large" placeholder="Select Chr" style={{ borderRadius: '8px' }}>
                                    {Array.from({ length: 22 }, (_, i) => i + 1).map(c => (
                                        <Select.Option key={c} value={c.toString()}>chr{c}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item
                                label={<span style={formLabelStyle}>Analysis type</span>}
                                name="mode"
                                rules={[{ required: true, message: 'Please confirm mode!' }]}
                            >
                                <Select size="large" placeholder="Select Mode" style={{ borderRadius: '8px' }}>
                                    <Select.Option value="1">Phasing & Imputation</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider style={{ margin: '24px 0' }} />

                    <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                        <span style={formLabelStyle}>Upload input VCF</span>
                        <Space wrap>
                            <Button
                                icon={<DownloadOutlined />}
                                href='https://gsrd-server.tos-cn-beijing.volces.com/example_chr20_GRCh37.vcf.gz'
                                style={{ borderRadius: '6px' }}
                            >
                                Example (GRCh37)
                            </Button>
                            <Button
                                icon={<DownloadOutlined />}
                                href='https://gsrd-server.tos-cn-beijing.volces.com/example_chr20_GRCh38.vcf.gz'
                                style={{ borderRadius: '6px' }}
                            >
                                Example (GRCh38)
                            </Button>
                            <Button
                                type="primary"
                                icon={<ExperimentOutlined />}
                                loading={demoButtonLoading}
                                disabled={isQuotaLimited}
                                onClick={() => setIsModalOpen(true)}
                                style={{ borderRadius: '6px', backgroundColor: '#13769a', borderColor: '#13769a' }}
                            >
                                Use Demo Data
                            </Button>
                        </Space>
                    </div>

                    <Form.Item
                        name="uploadFile"
                        valuePropName="fileList"
                        getValueFromEvent={normFile}
                        rules={[{ required: true, message: 'Please upload a file!' }]}
                    >
                        <Dragger {...uploadProps}
                            maxCount={1}
                            customRequest={customRequestFunc}
                            style={{
                                borderRadius: '16px',
                                padding: '32px',
                                backgroundColor: '#fafafa',
                                border: '2px dashed #d9d9d9',
                                transition: 'border-color 0.3s'
                            }}
                        >
                            <p className="ant-upload-drag-icon">
                                <InboxOutlined style={{ fontSize: '48px', color: '#055878' }} />
                            </p>
                            <p className="ant-upload-text" style={{ fontSize: '20px', fontWeight: '700', color: '#444' }}>
                                Click to upload or drag a VCF file here
                            </p>
                            <p className="ant-upload-hint" style={{ color: '#888' }}>
                                Accepted format: <strong>.vcf.gz</strong> • Max size: <strong>100MB</strong>
                            </p>
                        </Dragger>
                    </Form.Item>

                    {/* 两个协议项拆成独立字段，必须全部勾选后才允许提交 */}
                    <div style={{
                        marginTop: '32px',
                        padding: '18px 20px',
                        border: '1px solid #d8e8ee',
                        borderRadius: '14px',
                        backgroundColor: '#f8fcfd'
                    }}>
                        <Form.Item
                            name="dataComplianceAgreement"
                            valuePropName="checked"
                            rules={[{ validator: (_, value) => value ? Promise.resolve() : Promise.reject('Please confirm the data compliance agreement!') }]}
                            style={{ marginBottom: '12px' }}
                        >
                            <Checkbox style={{ display: 'flex', alignItems: 'flex-start', fontSize: '14px', color: '#365766', lineHeight: 1.6 }}>
                                I confirm that the submitted data comply with all applicable data-use, privacy, and confidentiality requirements.
                            </Checkbox>
                        </Form.Item>

                        <div style={{ height: '1px', margin: '0 0 12px 28px', backgroundColor: '#e5f0f3' }} />

                        <Form.Item
                            name="incidentReportAgreement"
                            valuePropName="checked"
                            rules={[{ validator: (_, value) => value ? Promise.resolve() : Promise.reject('Please confirm the incident reporting agreement!') }]}
                            style={{ marginBottom: 0 }}
                        >
                            <Checkbox style={{ display: 'flex', alignItems: 'flex-start', fontSize: '14px', color: '#365766', lineHeight: 1.6 }}>
                                I agree to report any inadvertent data release, security breach, or other data-management incident of which I become aware..
                            </Checkbox>
                        </Form.Item>
                    </div>

                    <Form.Item style={{ textAlign: 'center', marginTop: '40px' }}>
                        <Button
                            // 当前提交入口按需求保持禁用状态
                            // disabled={true}
                            disabled={isQuotaLimited}
                            type="primary"
                            size="large"
                            htmlType="submit"
                            loading={submitButtonLoading}
                            style={{
                                minWidth: '240px',
                                height: '56px',
                                borderRadius: '28px',
                                fontWeight: '700',
                                fontSize: '20px',
                                boxShadow: '0 4px 14px rgba(5, 88, 120, 0.2)'
                            }}
                        >
                            Submit analysis
                        </Button>
                    </Form.Item>
                </Form>

                <Modal
                    title={<span style={{ fontWeight: 700 }}>Confirm Demo Data</span>}
                    open={isModalOpen}
                    okText="Proceed"
                    okButtonProps={{ loading: demoButtonLoading }}
                    onOk={() => {
                        if (isQuotaLimited) {
                            messageApi.warning('You have reached the daily imputation submission limit. Please try again tomorrow.');
                            return;
                        }
                        messageApi.open({ type: 'loading', content: 'Fetching demo data...', });
                        setDemoButtonLoading(true)
                        axios.post(API_ROUTES.api_gene_send, { testFlag: 1 }, {
                            headers: { Authorization: localStorage.getItem('userInfo'), },
                        })
                            .then(response => {
                                if (response.data.code === 200) {
                                    messageApi.success('Demo data submitted successfully!');
                                    setImputationQuota(response.data.data?.quota);
                                    onPageChange('upload');
                                } else {
                                    messageApi.error(response.data.msg || 'Submission failed. Please try again.');
                                    if (response.data.data) {
                                        setImputationQuota(response.data.data);
                                    }
                                }
                            })
                            .catch(() => messageApi.error('An error occurred. Please try again.'))
                            .finally(() => setDemoButtonLoading(false));
                    }}
                    onCancel={() => setIsModalOpen(false)}
                    centered
                    style={{ borderRadius: '12px' }}
                >
                    <p>Would you like to use the pre-configured test data to explore the imputation pipeline?</p>
                </Modal>
            </Col>
        </Row>
    )
}
